// User profile: financial inputs + life-context that drives the "smart
// questions" engine. Stored encrypted in the local Vault only.

import type { EnblocSnapshot } from "../signals/enbloc";

export interface Profile {
  // --- What the user is evaluating (drives which tabs/content show) ---
  interests: { hdb: boolean; private: boolean; car: boolean };

  // --- Core financials (monthly SGD unless noted) ---
  ageYears: number;
  monthlyGrossIncome: number; // total household gross monthly income
  cashSavings: number; // liquid cash
  cpfOaBalance: number; // CPF Ordinary Account (usable for property)
  otherLiquidAssets: number; // stocks/bonds you'd deploy
  monthlyDebtRepayments: number; // existing loans (car, personal, etc.)
  existingHomeLoanMonthly: number; // 0 if none

  // --- Buyer profile (drives ABSD; app scope is Singapore Citizens and PRs) ---
  residency?: "SC" | "PR"; // undefined = SC
  married?: boolean; // buying jointly with a spouse (higher ABSD profile applies; SC-couple remissions)
  spouseResidency?: "SC" | "PR";
  useAgent?: boolean; // undefined = true; false = DIY (no agent fees on either side)

  // --- Holdings (affect stamp duty, loan caps) ---
  ownsResidentialProperty: boolean; // deprecated: "owns" is now derived from currentPropertyValue > 0
  // When you already own, what the next purchase is: sell the current home and
  // replace it ("upgrade", default) or keep it and add a second ("second").
  buyIntent?: "upgrade" | "second";
  currentPropertyValue: number; // estimated market value of property owned now
  currentPropertyOutstandingLoan: number; // outstanding mortgage on it
  currentPropertyType?: string; // series id of its market segment, for the sell signal
  cpfUsedInCurrent: number; // exact CPF refund (principal + accrued interest) from the CPF dashboard; 0 = estimate it
  cpfLumpSumUsed?: number; // CPF used at purchase (downpayment, stamp duty, legal) — for the accrued-interest estimate
  cpfMonthlyInstalment?: number; // monthly CPF towards the current mortgage — for the estimate
  currentPropertyYearsHeld: number; // for SSD (3 or 4 yrs depending on purchase date) and HDB MOP (< 5y)
  // True once the user has actually answered "years held". A stored 0 is
  // otherwise indistinguishable from "never asked" — see yearsHeldKnown().
  currentPropertyYearsHeldSet?: boolean;
  numberOfCars: number;
  currentCarValue: number; // estimated resale value of current car
  currentCarOutstandingLoan: number; // outstanding car loan
  currentCarCategory?: "cat-a" | "cat-b" | "cat-e"; // COE category of current car

  // --- Life context (drives smart questions / re-profiling) ---
  household: {
    adults: number;
    children: number;
    childrenSchoolingStage?: "none" | "preschool" | "primary" | "secondary" | "tertiary";
  };
  workLocationArea?: string; // e.g. "One-North", "CBD", "Changi"
  spouseWorkLocationArea?: string; // partner's workplace, for combined commute
  childSchool?: string; // the specific school to prioritise (1–2km zones), for commute + shortlisting
  transitDependent?: boolean; // "I don't drive" — elevate MRT walking access in the listing verdict
  preferredRegions: string[]; // property search focus, e.g. ["Queenstown","Clementi"]

  // --- Targets under evaluation ---
  // The two size targets that parametrise each asset class's analysis:
  //   • private is priced per-psf, so a size (sqft) turns $psf into a unit price
  //   • HDB is priced as a whole flat, so the "size" is the flat type
  targetPrivateAreaSqft: number; // size assumption to turn $psf into a unit price
  targetHdbFlatType?: string; // e.g. "4 ROOM"; "" = any. Shared across HDB panels.
  targetHdbRemainingLease?: number; // remaining lease (years) of the HDB flat under evaluation; drives CPF/LTV/tenure via the lease-decay engine
  priceUnit?: "psf" | "psm"; // display basis for private unit prices
  targetHdbType?: string; // deprecated (unused) — superseded by targetHdbFlatType
  targetPrivate?: boolean; // deprecated (unused)
  targetCarPrice?: number; // manual entry (no legal free model-level feed)
  targetCarCategory?: "cat-a" | "cat-b" | "cat-e";

  // Prevailing loan rates you're quoted. Eligibility is always stress-tested at
  // the MAS floor; these drive the *estimated actual* monthly repayment.
  mortgageRatePct: number; // your bank's floating/fixed package rate
  mortgageRateManual?: boolean; // true = use mortgageRatePct; else live 3M SORA + spread
  carLoanRatePct: number; // car loan flat rate

  // --- Persisted UI filter memory (local-only, per profile) ---
  // Remembered so search filters and the planner restore on return.
  hdbFilterTown?: string;
  hdbFilterFlatType?: string;
  hdbFilterMinArea?: number;
  hdbFilterMinLease?: number;
  hdbFilterMaxPrice?: number; // undervalued HDB price cap
  privateDistrictPick?: string;
  privFilterType?: string; // private property-type filter ("" = any)
  privFilterTenure?: "" | "freehold" | "leasehold";
  privFilterMaxPrice?: number;
  movePlanScenario?: string; // MoveScenario id (kept as string to avoid a cycle)
  movePlanNextClass?: "hdb" | "private";
  movePlanNextPrice?: number;

  // Onboarding: set once the guided first-run setup has been completed/skipped.
  onboarded?: boolean;

  // Set once the user has acknowledged the "longitudinal trends are ready"
  // prompt, so it fires once rather than on every visit.
  longitudinalNotified?: boolean;

  // Agent-free transaction guide state.
  diyMode?: "move" | "single"; // "move" composes the sell+buy playbook from the planner
  diyFlow?: string; // selected flow id, e.g. "hdb-buy"
  diyPrice?: number; // assumed price for the cost estimate
  diyProgress?: Record<string, boolean>; // "<flowId>:<stepId>" -> done

  // Shortlist: projects/units saved from the under-valued panels to evaluate.
  shortlist?: ShortlistItem[];
  // Ids of shortlisted listings currently selected for the side-by-side compare
  // view. Persisted so the comparison set survives across the buying process.
  compareIds?: string[];

  updatedAt: string;
}

// Strata / MCST financial-health inputs for a private unit. There is no public
// feed for these — the buyer supplies them from the AGM audited accounts or the
// BMSMA §47 certificate. All optional; the score degrades gracefully.
export interface StrataInputs {
  units?: number; // number of units in the development
  monthlyContribution?: number; // this unit's monthly maintenance + sinking, S$
  sinkingBalance?: number; // sinking-fund balance from the balance sheet, S$
  specialLevy3y?: boolean; // a special levy raised in the last 3 years
  arrearsPct?: number; // % of contributions/owners in arrears (from the AGM)
  selfManaged?: boolean; // self-managed vs a professional managing agent
  ageYears?: number; // development age (drives capital-works burden)
}

// A frozen snapshot of how a listing sat against recent transactions at the
// moment it was saved. Frozen (not recomputed live) because the underlying URA
// cache rolls off and HDB months shift — freezing keeps the decision record
// honest and reproducible; a manual refresh re-pulls it on demand.
export interface CompSnapshot {
  asOf: string; // ISO date captured / last refreshed
  unit: "psf" | "psm"; // rate basis (private = psf, HDB = psm)
  listingRate: number; // the listing's own $psf / $psm (asking ÷ area)
  medianRate: number; // 6-month median of the comparable set
  p25Rate?: number;
  p75Rate?: number;
  minRate?: number;
  maxRate?: number;
  n: number; // comparable transactions in the window
  months: number; // window length
  percentile: number; // 0..1 — listing's rate vs the field (higher = pricier)
  pctVsMedian: number; // listingRate / medianRate - 1
  latestMonth?: string | null;
  scope: string; // human label for the comparable set (project / town + type)
  // Enough context to re-run the comp query on a manual refresh.
  query?: { district?: string; project?: string; street?: string; town?: string; flatType?: string };
}

// A saved under-valued unit/project the user wants to evaluate and act on.
export interface ShortlistItem {
  id: string;
  klass: "hdb" | "private";
  label: string; // project / block + street
  locality: string; // town or district label
  propertyType?: string;
  areaSqm: number;
  refPrice: number; // the reference price (a transaction, or a listing's asking price)
  fairValue: number; // model fair value
  pctBelow: number; // gap vs fair value (negative = below/cheap)
  tenure?: string;
  savedAt: string;
  source?: "flagged" | "listing"; // model-flagged unit vs a user-entered listing
  verdict?: "buy" | "consider" | "pass"; // blended signal for a user-entered listing
  strata?: StrataInputs; // optional MCST financial-health inputs (private only)
  mrt?: { name?: string; code?: string; distanceM?: number; walkMin?: number }; // nearest MRT for transit access
  comps?: CompSnapshot; // frozen "listing vs recent transactions" evidence
  enbloc?: EnblocSnapshot; // last-fetched en-bloc potential snapshot (private units)
}

/**
 * Whether we actually know how long the current property has been held. Unknown
 * must never be treated as 0: 0 would silently apply the top SSD rate, the HDB
 * MOP block and a zero CPF-instalment refund (UX_FINDINGS U-02). Any positive
 * value must have been entered, so it counts as known for older profiles.
 */
export function yearsHeldKnown(p: Pick<Profile, "currentPropertyYearsHeld" | "currentPropertyYearsHeldSet">): boolean {
  return p.currentPropertyYearsHeldSet === true || (p.currentPropertyYearsHeld ?? 0) > 0;
}

export function emptyProfile(): Profile {
  return {
    // Cars are opt-in (owner decision D-3, 2026-10-05): a housing-first app.
    interests: { hdb: true, private: true, car: false },
    ageYears: 35,
    monthlyGrossIncome: 0,
    cashSavings: 0,
    cpfOaBalance: 0,
    otherLiquidAssets: 0,
    monthlyDebtRepayments: 0,
    existingHomeLoanMonthly: 0,
    ownsResidentialProperty: false,
    currentPropertyValue: 0,
    currentPropertyOutstandingLoan: 0,
    cpfUsedInCurrent: 0,
    currentPropertyYearsHeld: 0,
    numberOfCars: 0,
    currentCarValue: 0,
    currentCarOutstandingLoan: 0,
    household: { adults: 1, children: 0, childrenSchoolingStage: "none" },
    preferredRegions: [],
    targetPrivateAreaSqft: 900,
    mortgageRatePct: 3.5, // current SG floating-rate ballpark — adjust to your quote
    carLoanRatePct: 2.78,
    updatedAt: new Date().toISOString(),
  };
}

// --- Smart-questions engine ------------------------------------------------
// Life events that, when changed, should prompt the user to re-evaluate
// assumptions (work location, schooling, household size, holdings).
export interface SmartQuestion {
  id: string;
  prompt: string;
  why: string; // why it matters for feasibility
  field: keyof Profile | string;
  reprofileTrigger: boolean; // a material change should invite re-evaluation
}

export const SMART_QUESTIONS: SmartQuestion[] = [
  {
    id: "work-location",
    prompt: "Has your (or your partner's) main work location changed?",
    why: "Commute anchors which towns/regions are realistic — it reshapes the shortlist and the price band you should watch.",
    field: "workLocationArea",
    reprofileTrigger: true,
  },
  {
    id: "children-schooling",
    prompt: "Any change to your children's schooling stage or preferred school?",
    why: "1km/2km school priority zones strongly influence target locations and therefore price bands.",
    field: "household.childrenSchoolingStage",
    reprofileTrigger: true,
  },
  {
    id: "household-size",
    prompt: "Has your household size changed (marriage, new child, elderly parents)?",
    why: "Drives flat-type sizing (e.g. 4- vs 5-room) and whether a car becomes necessary.",
    field: "household",
    reprofileTrigger: true,
  },
  {
    id: "income-change",
    prompt: "Material change to household income?",
    why: "Directly changes loan eligibility (MSR/TDSR) and buying power.",
    field: "monthlyGrossIncome",
    reprofileTrigger: true,
  },
  {
    id: "new-holding",
    prompt: "Bought or sold a property or car since last review?",
    why: "Changes ABSD tier, LTV limits, and monthly debt servicing.",
    field: "ownsResidentialProperty",
    reprofileTrigger: true,
  },
];

// Detect which smart questions warrant a re-profile prompt by diffing profiles.
export function detectReprofilePrompts(prev: Profile | null, next: Profile): SmartQuestion[] {
  if (!prev) return [];
  const out: SmartQuestion[] = [];
  const changed = (a: unknown, b: unknown) => JSON.stringify(a) !== JSON.stringify(b);
  if (changed(prev.workLocationArea, next.workLocationArea))
    out.push(SMART_QUESTIONS[0]);
  if (changed(prev.household.childrenSchoolingStage, next.household.childrenSchoolingStage))
    out.push(SMART_QUESTIONS[1]);
  if (
    prev.household.adults !== next.household.adults ||
    prev.household.children !== next.household.children
  )
    out.push(SMART_QUESTIONS[2]);
  if (Math.abs(next.monthlyGrossIncome - prev.monthlyGrossIncome) / (prev.monthlyGrossIncome || 1) > 0.1)
    out.push(SMART_QUESTIONS[3]);
  if (
    (prev.currentPropertyValue > 0) !== (next.currentPropertyValue > 0) ||
    (prev.currentCarValue > 0) !== (next.currentCarValue > 0)
  )
    out.push(SMART_QUESTIONS[4]);
  return out;
}
